import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { ConfirmDialog, EmptyState, InlineMessage, SectionCard, StatusChip } from './ui';

interface PlanInventory {
  tracked: boolean;
  total: number;
  levels: Array<{ locationId: string; locationName: string; quantity: number }>;
}

interface PlanMember {
  productId: string;
  sku: string;
  optionValue: string;
  inventory: PlanInventory | null;
  action: 'merge' | 'skip';
  step?: 'full' | 'archive_pending' | 'redirect_only';
  reason?: string;
}

interface PlanGroup {
  groupId: string;
  name: string;
  optionName: string;
  survivor: { productId: string; sku: string; shopifyProductId: string; title: string; newTitle?: string | null } | null;
  members: PlanMember[];
  skippedReason?: string;
}

interface MerchantRow {
  shopifyVariantId: string;
  sku: string;
  barcode: string;
  productId?: string;
  productSku?: string;
  productName?: string;
  matchedBy?: 'sku' | 'barcode';
  action: 'merge' | 'approve' | 'review' | 'skip';
  kind?: 'link' | 'replace_duplicate';
  step?: 'full' | 'archive_pending' | 'redirect_only';
  archiveDuplicate?: boolean;
  reason?: string;
  candidateProductIds?: string[];
}

interface MerchantListing {
  shopifyProductId: string;
  title: string;
  variantCount: number;
  variants: MerchantRow[];
}

interface MerchantPass {
  listings: MerchantListing[];
  totals: { listings: number; mergeable: number; approve: number; review: number; dismissed: number };
  approvalsAvailable: boolean;
  searchedProducts: number;
  nextCursor: string | null;
  syncMode: string;
}

interface Plan {
  redirectsAvailable: boolean;
  groups: PlanGroup[];
  groupsNextCursor?: string | null;
  totals: { groups: number; mergeable: number; skipped: number };
  merchant?: MerchantPass;
}

interface MemberResult {
  productId: string;
  sku: string;
  status: 'merged' | 'merged_without_redirect' | 'skipped' | 'failed' | 'not_attempted';
  reason?: string;
  error?: string;
  warnings?: string[];
}

interface ApplyResult {
  groups: Array<{
    groupId: string;
    name: string;
    status: 'merged' | 'merged_without_redirect' | 'partial' | 'failed' | 'skipped';
    members: MemberResult[];
  }>;
  totals: { merged: number; skipped: number; failed: number };
  merchant?: {
    listings: Array<{
      shopifyProductId: string;
      title: string;
      variants: Array<{
        shopifyVariantId: string;
        productId: string;
        sku: string;
        kind: 'link' | 'replace_duplicate';
        status: 'merged' | 'merged_without_redirect' | 'failed';
        crosswalk?: { mappingId: string; outcome: 'created' | 'updated' | 'unchanged' };
        error?: string;
        warnings?: string[];
      }>;
    }>;
    totals: { merged: number; mergedWithoutRedirect: number; failed: number };
  };
}

/** What a preview or a run covers; mirrors the server's `ConsolidationScope`. */
interface Scope {
  productIds?: string[];
  after?: string;
  groupAfter?: string;
  kinds?: Array<'variant_groups' | 'merchant_variants'>;
  approveVariantIds?: string[];
}

/** Several runs (one per kind for a selection) read as one result. */
function combineResults(results: ApplyResult[]): ApplyResult {
  const merchant = results.map((r) => r.merchant).filter((m): m is NonNullable<ApplyResult['merchant']> => Boolean(m));
  return {
    groups: results.flatMap((r) => r.groups),
    totals: {
      merged: results.reduce((n, r) => n + r.totals.merged, 0),
      skipped: results.reduce((n, r) => n + r.totals.skipped, 0),
      failed: results.reduce((n, r) => n + r.totals.failed, 0),
    },
    ...(merchant.length > 0
      ? {
          merchant: {
            listings: merchant.flatMap((m) => m.listings),
            totals: {
              merged: merchant.reduce((n, m) => n + m.totals.merged, 0),
              mergedWithoutRedirect: merchant.reduce((n, m) => n + m.totals.mergedWithoutRedirect, 0),
              failed: merchant.reduce((n, m) => n + m.totals.failed, 0),
            },
          },
        }
      : {}),
  };
}

type MerchantFilter = 'all' | 'merge' | 'approve' | 'review';

export interface ShopifyVariantConsolidationSectionProps {
  integrationId: string;
}

const T = 'integrations.variantConsolidation';

async function call<R>(path: string, body: unknown, failedKey: string, t: (k: string) => string): Promise<R> {
  const response = await makeApiCall(path, {
    method: 'POST',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw Object.assign(new Error(json?.message || json?.error || t(failedKey)), { status: response.status });
  }
  return json.data as R;
}

/**
 * Merge colour and size listings (flag `variant_listing_consolidation`), and, with
 * `merchant_variant_merge`, link catalog products to variants the store created itself. The store
 * previews, picks what to merge (or merges everything the preview lists), and can come back for the
 * next batch; a run covers only what was chosen. Hidden with the flag off.
 */
const ShopifyVariantConsolidationSection: React.FC<ShopifyVariantConsolidationSectionProps> = ({ integrationId }) => {
  const { t } = useAppTranslation();
  const { enabled } = useOrgFeatureFlag('variant_listing_consolidation');
  const { enabled: merchantEnabled } = useOrgFeatureFlag('merchant_variant_merge');
  const [confirm, setConfirm] = useState<'selected' | 'all' | 'single' | null>(null);
  /** The barcode match a row's own "Approve & merge" button confirms. */
  const [single, setSingle] = useState<MerchantRow | null>(null);
  const [result, setResult] = useState<ApplyResult | null>(null);
  const [previewScope, setPreviewScope] = useState<Scope>({});
  /** Products of merge rows and set members, by product id. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Barcode matches to approve, by Shopify variant id. */
  const [approvals, setApprovals] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<MerchantFilter>('all');
  const [search, setSearch] = useState('');
  const [dismissedNote, setDismissedNote] = useState<number | null>(null);

  const base = `/integrations/${integrationId}/shopify/variant-consolidation`;
  const preview = useMutation({
    mutationFn: (scope: Scope) => call<Plan>(`${base}/plan`, scope, `${T}.previewFailed`, t),
    onMutate: (scope) => {
      setPreviewScope(scope);
      setSelected(new Set());
      setApprovals(new Set());
    },
  });
  const dismiss = useMutation({
    mutationFn: (matches: Array<{ shopifyProductId: string; shopifyVariantId: string }>) =>
      call<{ dismissed: string[] }>(`${base}/dismiss`, { matches }, `${T}.merchant.dismissFailed`, t),
    onSuccess: (data) => {
      setDismissedNote(data.dismissed.length);
      preview.mutate(previewScope);
    },
  });
  const apply = useMutation({
    mutationFn: async (scopes: Scope[]) => {
      const results: ApplyResult[] = [];
      for (const scope of scopes) results.push(await call<ApplyResult>(`${base}/apply`, scope, `${T}.applyFailed`, t));
      return combineResults(results);
    },
    onSuccess: (data) => {
      setConfirm(null);
      setSingle(null);
      setDismissedNote(null);
      setResult(data);
      // Show what is left, for the same part of the store.
      preview.mutate(previewScope);
    },
    onError: () => setConfirm(null),
  });

  const plan = preview.data;
  const merchantRows = useMemo(
    () => (plan?.merchant?.listings || []).flatMap((listing) => listing.variants.map((row) => ({ listing, row }))),
    [plan],
  );
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return merchantRows.filter(({ listing, row }) => {
      if (filter !== 'all' && row.action !== filter) return false;
      if (!q) return true;
      return [listing.title, row.sku, row.barcode, row.productName, row.productSku]
        .some((value) => String(value || '').toLowerCase().includes(q));
    });
  }, [merchantRows, filter, search]);

  if (!enabled) return null;

  const reason = (code?: string) => (code ? t(`${T}.skipReason.${code}`) : '');
  /** Per-location quantities and the total; a level that does not exist is not listed, never shown as 0. */
  const stockText = (inventory: PlanInventory | null): string => {
    if (!inventory) return t(`${T}.stockUnknown`);
    if (!inventory.tracked) return t(`${T}.stockUntracked`);
    if (inventory.levels.length === 0) return t(`${T}.stockNone`);
    return t(`${T}.stockLocations`, {
      locations: inventory.levels.map((l) => `${l.locationName}: ${l.quantity}`).join(', '),
      total: inventory.total,
    });
  };

  const groupMergeIds = (group: PlanGroup) => group.members.filter((m) => m.action === 'merge').map((m) => m.productId);
  const mergeableMerchant = merchantRows.filter(({ row }) => row.action === 'merge' && row.productId);
  const approvableRows = merchantRows.filter(({ row }) => row.action === 'approve' && row.productId);
  // "Select all shown" covers both kinds of row the filter shows: merges by product, approvals by variant.
  const visibleMergeable = visibleRows.filter(({ row }) => row.action === 'merge' && row.productId).map(({ row }) => row.productId!);
  const visibleApprovable = visibleRows.filter(({ row }) => row.action === 'approve' && row.productId).map(({ row }) => row.shopifyVariantId);
  const visibleSelectable = visibleMergeable.length + visibleApprovable.length;
  const allVisibleSelected = visibleSelectable > 0
    && visibleMergeable.every((id) => selected.has(id))
    && visibleApprovable.every((id) => approvals.has(id));
  const someVisibleSelected = visibleMergeable.some((id) => selected.has(id)) || visibleApprovable.some((id) => approvals.has(id));
  const toggleIn = (setter: React.Dispatch<React.SetStateAction<Set<string>>>) => (ids: string[], on: boolean) => {
    setter((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });
  };
  const toggle = toggleIn(setSelected);
  const toggleApproval = toggleIn(setApprovals);

  const anythingMergeable = Boolean(plan && (plan.totals.mergeable > 0 || (plan.merchant?.totals.mergeable || 0) > 0));
  /** Counts for the confirmation: what the chosen run will do. */
  const summary = (() => {
    if (confirm === 'single' && single) {
      return { links: 0, duplicates: 0, groupMembers: 0, approvals: 1, approvalRows: [single] };
    }
    const covered = (productId?: string) => confirm === 'all' || Boolean(productId && selected.has(productId));
    const rows = mergeableMerchant.filter(({ row }) => covered(row.productId)).map(({ row }) => row);
    const approvalRows = confirm === 'selected' ? approvableRows.filter(({ row }) => approvals.has(row.shopifyVariantId)).map(({ row }) => row) : [];
    return {
      links: rows.filter((r) => r.kind === 'link').length,
      duplicates: rows.filter((r) => r.kind === 'replace_duplicate').length,
      groupMembers: (plan?.groups || []).reduce((n, g) => n + g.members.filter((m) => m.action === 'merge' && covered(m.productId)).length, 0),
      approvals: approvalRows.length,
      approvalRows,
    };
  })();
  /**
   * "Merge listings" re-runs the preview's own scope and approves nothing. A selection runs each kind
   * on its own products, so a set member is merged as its set row showed, never claimed by the other
   * pass; the barcode matches it approves ride the merchant run, named by variant.
   */
  const runScopes = (): Scope[] => {
    if (confirm === 'single' && single?.productId) {
      return [{ productIds: [single.productId], kinds: ['merchant_variants'], approveVariantIds: [single.shopifyVariantId] }];
    }
    if (confirm !== 'selected') return [previewScope];
    const merchantIds = new Set(mergeableMerchant.map(({ row }) => row.productId!));
    const chosen = Array.from(selected);
    const chosenApprovals = summary.approvalRows;
    const chosenMerchant = [...chosen.filter((id) => merchantIds.has(id)), ...chosenApprovals.map((r) => r.productId!)];
    const chosenGroups = chosen.filter((id) => !merchantIds.has(id));
    return [
      ...(chosenMerchant.length > 0
        ? [{
            productIds: chosenMerchant,
            kinds: ['merchant_variants' as const],
            ...(chosenApprovals.length > 0 ? { approveVariantIds: chosenApprovals.map((r) => r.shopifyVariantId) } : {}),
          }]
        : []),
      ...(chosenGroups.length > 0 ? [{ productIds: chosenGroups, kinds: ['variant_groups' as const] }] : []),
    ];
  };
  const dismissRows = (variantIds: string[]) => {
    const byVariant = new Map(approvableRows.map(({ listing, row }) => [row.shopifyVariantId, listing.shopifyProductId]));
    dismiss.mutate(variantIds.filter((id) => byVariant.has(id)).map((id) => ({ shopifyProductId: byVariant.get(id)!, shopifyVariantId: id })));
  };
  const pickedCount = selected.size + approvals.size;

  const merchantOutcome = (row: MerchantRow): { label: string; tone: 'info' | 'caution' | 'neutral' } => {
    if (row.action === 'merge') {
      const key = row.step && row.step !== 'full' ? row.step : row.kind || 'link';
      return { label: t(`${T}.merchant.outcome.${key}`), tone: 'info' };
    }
    if (row.action === 'approve') return { label: t(`${T}.merchant.outcome.approve`), tone: 'info' };
    if (row.action === 'review') return { label: t(`${T}.merchant.outcome.review`), tone: 'caution' };
    return { label: t(`${T}.merchant.outcome.skip`), tone: 'neutral' };
  };

  return (
    <SectionCard
      title={t(`${T}.title`)}
      subtitle={t(merchantEnabled ? `${T}.subtitleMerchant` : `${T}.subtitle`)}
      dense
      padded
    >
      <Stack spacing={2}>
        <Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => preview.mutate({})}
            disabled={preview.isPending || apply.isPending}
            startIcon={preview.isPending ? <CircularProgress size={14} /> : undefined}
          >
            {preview.isPending ? t(`${T}.previewing`) : t(`${T}.preview`)}
          </Button>
        </Box>

        {preview.isError && <InlineMessage severity="error">{(preview.error as Error).message}</InlineMessage>}
        {/* 409: a sync or another merge holds the integration; the server's message says to try again. */}
        {apply.isError && (
          <InlineMessage severity={(apply.error as Error & { status?: number }).status === 409 ? 'warning' : 'error'}>
            {(apply.error as Error).message}
          </InlineMessage>
        )}

        {plan && plan.groups.length === 0 && !plan.merchant?.listings.length && (
          <InlineMessage severity="info">{t(plan.merchant ? `${T}.nothingMerchant` : `${T}.nothing`)}</InlineMessage>
        )}
        {plan && !plan.redirectsAvailable && anythingMergeable && (
          <InlineMessage severity="warning">{t(`${T}.redirectsUnavailable`)}</InlineMessage>
        )}

        {plan && plan.groups.length > 0 && (
          <>
            <Typography variant="body2">
              {t(`${T}.summary`, { groups: plan.totals.groups, mergeable: plan.totals.mergeable, skipped: plan.totals.skipped })}
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>{t(`${T}.columns.group`)}</TableCell>
                    <TableCell>{t(`${T}.columns.survivor`)}</TableCell>
                    <TableCell>{t(`${T}.columns.members`)}</TableCell>
                    <TableCell>{t(`${T}.columns.skipped`)}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.groups.map((group) => {
                    const ids = groupMergeIds(group);
                    const all = ids.length > 0 && ids.every((id) => selected.has(id));
                    return (
                      <TableRow key={group.groupId} sx={{ verticalAlign: 'top' }}>
                        <TableCell padding="checkbox">
                          {ids.length > 0 && (
                            <Checkbox
                              size="small"
                              checked={all}
                              indeterminate={!all && ids.some((id) => selected.has(id))}
                              onChange={() => toggle(ids, !all)}
                              inputProps={{ 'aria-label': t(`${T}.selection.selectGroup`, { name: group.name }) }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{group.name}</TableCell>
                        <TableCell>
                          {group.survivor ? `${group.survivor.sku}` : '-'}
                          {group.survivor?.newTitle && (
                            <Typography variant="body2" color="text.secondary">
                              {t(`${T}.renamedTo`, { title: group.survivor.newTitle })}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {group.members
                            .filter((m) => m.action === 'merge')
                            .map((m) => (
                              <Typography key={m.productId} variant="body2">
                                {m.step && m.step !== 'full'
                                  ? t(`${T}.memberStep.${m.step}`, { sku: m.sku, value: m.optionValue })
                                  : t(`${T}.member`, { sku: m.sku, value: m.optionValue, stock: stockText(m.inventory) })}
                              </Typography>
                            ))}
                        </TableCell>
                        <TableCell>
                          {group.skippedReason && (
                            <Typography variant="body2" color="text.secondary">
                              {t(`${T}.groupSkipped`, { reason: reason(group.skippedReason) })}
                            </Typography>
                          )}
                          {group.members
                            .filter((m) => m.action === 'skip' && m.reason !== 'already_on_survivor')
                            .map((m) => (
                              <Typography key={m.productId} variant="body2" color="text.secondary">
                                {m.sku}: {reason(m.reason)}
                              </Typography>
                            ))}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            {plan.groupsNextCursor && (
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {t(`${T}.nextGroupsNote`)}
                </Typography>
                <Button
                  size="small"
                  onClick={() => preview.mutate({ ...previewScope, groupAfter: plan.groupsNextCursor! })}
                  disabled={preview.isPending || apply.isPending}
                >
                  {t(`${T}.nextGroups`)}
                </Button>
              </Stack>
            )}
          </>
        )}

        {plan?.merchant && (plan.merchant.listings.length > 0 || plan.merchant.nextCursor) && (
          <Stack spacing={1}>
            <Typography variant="subtitle2">{t(`${T}.merchant.title`)}</Typography>
            <Typography variant="body2">
              {t(`${T}.merchant.summary`, {
                listings: plan.merchant.totals.listings,
                mergeable: plan.merchant.totals.mergeable,
                approve: plan.merchant.totals.approve,
                review: plan.merchant.totals.review,
              })}
            </Typography>
            {plan.merchant.totals.dismissed > 0 && (
              <Typography variant="body2" color="text.secondary">
                {t(`${T}.merchant.dismissedHidden`, { count: plan.merchant.totals.dismissed })}
              </Typography>
            )}
            {!plan.merchant.approvalsAvailable && (
              <Typography variant="body2" color="text.secondary">{t(`${T}.merchant.approvalsNeedCrosswalk`)}</Typography>
            )}
            {dismissedNote !== null && (
              <InlineMessage severity="info">{t(`${T}.merchant.dismissedNote`, { count: dismissedNote })}</InlineMessage>
            )}
            {dismiss.isError && <InlineMessage severity="error">{(dismiss.error as Error).message}</InlineMessage>}
            <Typography variant="body2" color="text.secondary">
              {t(`${T}.merchant.syncMode`, { mode: t(`${T}.merchant.modes.${plan.merchant.syncMode}`, { defaultValue: plan.merchant.syncMode }) })}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={filter}
                onChange={(_e, next: MerchantFilter | null) => next && setFilter(next)}
                aria-label={t(`${T}.merchant.filterAria`)}
              >
                <ToggleButton value="all">{t(`${T}.merchant.filterAll`, { count: merchantRows.length })}</ToggleButton>
                <ToggleButton value="merge">{t(`${T}.merchant.filterMerge`, { count: plan.merchant.totals.mergeable })}</ToggleButton>
                {plan.merchant.approvalsAvailable && (
                  <ToggleButton value="approve">{t(`${T}.merchant.filterApprove`, { count: plan.merchant.totals.approve })}</ToggleButton>
                )}
                <ToggleButton value="review">{t(`${T}.merchant.filterReview`, { count: plan.merchant.totals.review })}</ToggleButton>
              </ToggleButtonGroup>
              <TextField
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t(`${T}.merchant.search`)}
                inputProps={{ 'aria-label': t(`${T}.merchant.search`) }}
              />
              {visibleSelectable > 0 && (
                <Stack direction="row" alignItems="center">
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={!allVisibleSelected && someVisibleSelected}
                    onChange={() => {
                      toggle(visibleMergeable, !allVisibleSelected);
                      toggleApproval(visibleApprovable, !allVisibleSelected);
                    }}
                    inputProps={{ 'aria-label': t(`${T}.selection.selectVisible`) }}
                  />
                  <Typography variant="body2">{t(`${T}.selection.selectVisible`)}</Typography>
                </Stack>
              )}
            </Stack>
            <Box sx={{ maxHeight: 560, overflow: 'auto' }}>
              {visibleRows.length === 0 ? (
                <EmptyState size="compact" title={t(search.trim() ? `${T}.merchant.emptySearch` : `${T}.merchant.empty.${filter}`, { q: search.trim() })} />
              ) : (
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>{t(`${T}.merchant.columns.listing`)}</TableCell>
                      <TableCell>{t(`${T}.merchant.columns.product`)}</TableCell>
                      <TableCell>{t(`${T}.merchant.columns.outcome`)}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRows.map(({ listing, row }) => {
                      const outcome = merchantOutcome(row);
                      const selectable = row.action === 'merge' && row.productId;
                      const approvable = row.action === 'approve' && row.productId;
                      return (
                        <TableRow key={`${listing.shopifyProductId}:${row.shopifyVariantId}`} sx={{ verticalAlign: 'top' }}>
                          <TableCell padding="checkbox">
                            {selectable && (
                              <Checkbox
                                size="small"
                                checked={selected.has(row.productId!)}
                                onChange={(e) => toggle([row.productId!], e.target.checked)}
                                inputProps={{ 'aria-label': t(`${T}.selection.selectRow`, { sku: row.sku || row.barcode }) }}
                              />
                            )}
                            {approvable && (
                              <Checkbox
                                size="small"
                                checked={approvals.has(row.shopifyVariantId)}
                                onChange={(e) => toggleApproval([row.shopifyVariantId], e.target.checked)}
                                inputProps={{ 'aria-label': t(`${T}.selection.selectApproval`, { sku: row.sku }) }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{listing.title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t(`${T}.merchant.identifiers`, {
                                sku: row.sku || t(`${T}.merchant.none`),
                                barcode: row.barcode || t(`${T}.merchant.none`),
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {row.productId ? (
                              <>
                                <Typography variant="body2">{row.productName}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {row.productSku}
                                  {row.matchedBy ? ` · ${t(`${T}.merchant.matchedBy.${row.matchedBy}`)}` : ''}
                                </Typography>
                              </>
                            ) : row.candidateProductIds && row.candidateProductIds.length > 1 ? (
                              <Typography variant="body2" color="text.secondary">
                                {t(`${T}.merchant.candidates`, { count: row.candidateProductIds.length })}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <StatusChip label={outcome.label} tone={outcome.tone} />
                            {row.action === 'approve' ? (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {t(`${T}.merchant.approveExplain.${row.kind === 'replace_duplicate' ? 'replace_duplicate' : 'link'}`, {
                                  barcode: row.barcode,
                                  product: row.productName,
                                  sku: row.sku,
                                })}
                              </Typography>
                            ) : row.reason && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {t(`${T}.merchant.reason.${row.reason}`)}
                              </Typography>
                            )}
                            {approvable && (
                              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={apply.isPending || dismiss.isPending}
                                  onClick={() => {
                                    setSingle(row);
                                    setConfirm('single');
                                  }}
                                  aria-label={t(`${T}.merchant.approveOneAria`, { sku: row.sku })}
                                >
                                  {t(`${T}.merchant.approveOne`)}
                                </Button>
                                <Button
                                  size="small"
                                  disabled={apply.isPending || dismiss.isPending}
                                  onClick={() => dismissRows([row.shopifyVariantId])}
                                  aria-label={t(`${T}.merchant.dismissOneAria`, { sku: row.sku })}
                                >
                                  {t(`${T}.merchant.dismissOne`)}
                                </Button>
                              </Stack>
                            )}
                            {row.kind === 'replace_duplicate' && row.archiveDuplicate && row.step === 'full' && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {t(`${T}.merchant.duplicateArchived`)}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                {t(`${T}.merchant.searched`, { count: plan.merchant.searchedProducts })}
              </Typography>
              {plan.merchant.nextCursor && (
                <Button
                  size="small"
                  onClick={() => preview.mutate({ after: plan.merchant!.nextCursor! })}
                  disabled={preview.isPending || apply.isPending}
                >
                  {t(`${T}.merchant.nextBatch`)}
                </Button>
              )}
            </Stack>
          </Stack>
        )}

        {plan && (anythingMergeable || pickedCount > 0) && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {pickedCount > 0 ? (
              <>
                <Typography variant="body2">
                  {t(`${T}.selection.picked`, { merge: selected.size, approve: approvals.size })}
                </Typography>
                <Button variant="contained" size="small" onClick={() => setConfirm('selected')} disabled={apply.isPending || dismiss.isPending}>
                  {t(approvals.size > 0 ? `${T}.selection.approveSelected` : `${T}.selection.mergeSelected`)}
                </Button>
                {approvals.size > 0 && (
                  <Button size="small" onClick={() => dismissRows(Array.from(approvals))} disabled={apply.isPending || dismiss.isPending}>
                    {t(`${T}.selection.dismissSelected`)}
                  </Button>
                )}
                <Button
                  size="small"
                  onClick={() => {
                    setSelected(new Set());
                    setApprovals(new Set());
                  }}
                >
                  {t(`${T}.selection.clear`)}
                </Button>
              </>
            ) : (
              <Button variant="contained" size="small" onClick={() => setConfirm('all')} disabled={apply.isPending}>
                {apply.isPending ? t(`${T}.applying`) : t(`${T}.apply`)}
              </Button>
            )}
            {pickedCount === 0 && approvableRows.length > 0 && (
              <Typography variant="body2" color="text.secondary">{t(`${T}.selection.approvalsNotIncluded`)}</Typography>
            )}
          </Stack>
        )}

        {result && (
          <Stack spacing={1}>
            <Typography variant="subtitle2">{t(`${T}.results.title`)}</Typography>
            <Typography variant="body2">
              {t(`${T}.results.totals`, {
                merged: result.totals.merged + (result.merchant?.totals.merged || 0),
                skipped: result.totals.skipped,
                failed: result.totals.failed + (result.merchant?.totals.failed || 0),
              })}
            </Typography>
            {result.groups.map((group) => (
              <InlineMessage
                key={group.groupId}
                severity={group.status === 'merged' ? 'success' : group.status === 'skipped' ? 'info' : 'warning'}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {group.name}
                  </Typography>
                  <StatusChip
                    label={t(`${T}.results.status.${group.status}`)}
                    tone={group.status === 'merged' ? 'positive' : group.status === 'skipped' ? 'neutral' : 'caution'}
                  />
                </Stack>
                {group.members.map((m) => (
                  <Typography key={m.productId} variant="body2">
                    {m.sku}: {t(`${T}.results.status.${m.status}`)}
                    {m.reason ? ` (${reason(m.reason)})` : ''}
                    {m.status === 'failed' && m.error ? ` - ${t(`${T}.results.failedAt`, { error: m.error })}` : ''}
                    {(m.warnings || []).map((w) => ` - ${w}`).join('')}
                  </Typography>
                ))}
              </InlineMessage>
            ))}
            {(result.merchant?.listings || []).map((listing) => {
              const failed = listing.variants.some((v) => v.status === 'failed');
              return (
                <InlineMessage key={listing.shopifyProductId} severity={failed ? 'warning' : 'success'}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{listing.title}</Typography>
                  {listing.variants.map((v) => (
                    <Typography key={v.shopifyVariantId} variant="body2">
                      {v.sku || v.productId}: {t(`${T}.results.status.${v.status}`)}
                      {v.crosswalk ? ` - ${t(`${T}.results.crosswalk.${v.crosswalk.outcome}`)}` : ''}
                      {v.status === 'failed' && v.error ? ` - ${t(`${T}.results.failedAt`, { error: v.error })}` : ''}
                      {(v.warnings || []).map((w) => ` - ${w}`).join('')}
                    </Typography>
                  ))}
                </InlineMessage>
              );
            })}
          </Stack>
        )}
      </Stack>

      <ConfirmDialog
        open={confirm !== null}
        title={t(`${T}.confirm.title`)}
        message={
          <Stack spacing={1}>
            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
              {summary.groupMembers > 0 && (
                <Typography variant="body2">{t(`${T}.confirm.summaryGroups`, { count: summary.groupMembers })}</Typography>
              )}
              {summary.links > 0 && (
                <Typography variant="body2">{t(`${T}.confirm.summaryLinks`, { count: summary.links })}</Typography>
              )}
              {summary.duplicates > 0 && (
                <Typography variant="body2">{t(`${T}.confirm.summaryDuplicates`, { count: summary.duplicates })}</Typography>
              )}
              {summary.approvals > 0 && (
                <Typography variant="body2">{t(`${T}.confirm.summaryApprovals`, { count: summary.approvals })}</Typography>
              )}
            </Box>
            {summary.approvalRows.slice(0, 5).map((r) => (
              <Typography key={r.shopifyVariantId} variant="body2" color="text.secondary">
                {t(`${T}.confirm.approvalLine`, { sku: r.sku, barcode: r.barcode, product: r.productName })}
              </Typography>
            ))}
            {summary.groupMembers > 0 && <Typography variant="body2">{t(`${T}.confirm.message`)}</Typography>}
            {summary.links + summary.duplicates + summary.approvals > 0 && plan?.merchant && (
              <Typography variant="body2">
                {t(`${T}.confirm.merchantUntouched`, {
                  mode: t(`${T}.merchant.modes.${plan.merchant.syncMode}`, { defaultValue: plan.merchant.syncMode }),
                })}
              </Typography>
            )}
            <Typography variant="body2">{t(`${T}.confirm.orders`)}</Typography>
            <Typography variant="body2" color="text.secondary">{t(`${T}.confirm.onlyChosen`)}</Typography>
          </Stack>
        }
        confirmLabel={t(`${T}.confirm.confirm`)}
        cancelLabel={t('common.cancel')}
        loading={apply.isPending}
        onConfirm={() => apply.mutate(runScopes())}
        onClose={() => {
          setConfirm(null);
          setSingle(null);
        }}
      />
    </SectionCard>
  );
};

export default ShopifyVariantConsolidationSection;
